import {
  applyMove,
  autoExchange,
  checkMove,
  chooseMove,
  createMatch,
  makeRng,
  publicView,
  submitExchange,
  timeoutMove,
  type CardId,
  type Match,
  type Move,
  type Seat,
} from '@basgan/rules';
import {
  buildViewMessage,
  FALLBACK_CHAT_PHRASES,
  deriveEvents,
  type GameEvent,
  type PublicSeat,
} from '@basgan/protocol';
import type { Transport, TransportHandlers } from './transport.js';

/**
 * Botlara karşı yerel oyun — tarayıcıda, sunucusuz.
 *
 * Colyseus odasıyla AYNI olay akışını ve aynı `ViewMessage` şeklini üretiyor
 * (`deriveEvents` / `buildViewMessage` paylaşılan pakette). Masa bileşeni
 * online mı yerel mi oynadığını bilmiyor.
 *
 * Bunun bir de ürün karşılığı var: uygulama internetsiz de oynanabiliyor,
 * yani App Store'un "web sitesi sarmalayıcısı" reddine düşmüyor.
 */

const HUMAN: Seat = 0;
const BOT_NAMES = ['Tansel', 'Efsane', 'Şölen'];

export interface LocalGameOptions {
  name: string;
  /** Bot hamlesinden önceki bekleme — insanlar takip edebilsin. */
  botDelayMs?: number;
  /** İnsan oyuncunun sırası için süre; 0 verilirse sayaç kapanır. */
  turnMs?: number;
  /** Kart değişimi seçimi için süre; 0 verilirse sayaç kapanır. */
  exchangeMs?: number;
  /**
   * Masaya oturma ile kartların dağıtılması arasındaki geri sayım.
   * Online masadan (5 sn) kısa: burada beklenecek kimse yok, süre yalnızca
   * "masaya oturdum" anını göstermek için. 0 verilirse hemen başlar.
   */
  startDelayMs?: number;
  /** Botlar ara sıra hazır mesaj yollasın mı? Testlerde kapatılıyor. */
  botChat?: boolean;
  /** Test ve tekrar oynatma için sabit tohum. */
  seed?: string;
}

/** Bot hamlesinden sonra mesaj yollama olasılığı. */
const BOT_CHAT_CHANCE = 0.14;
/** Aynı botun iki mesajı arasındaki en kısa süre. */
const BOT_CHAT_COOLDOWN_MS = 7000;

export function createLocalGame(
  options: LocalGameOptions,
  handlers: TransportHandlers,
): Transport {
  const botDelayMs = options.botDelayMs ?? 850;
  const turnMs = options.turnMs ?? 15_000;
  const exchangeMs = options.exchangeMs ?? 20_000;
  const startDelayMs = options.startDelayMs ?? 3000;
  const botChat = options.botChat ?? true;
  const seed = options.seed ?? randomSeed();

  let match: Match = createMatch(seed);
  let seq = 0;
  let deadline: number | null = null;
  let deadlineTotal: number | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  /** Başlangıç geri sayımı — `timer`'dan ayrı, sıra zamanlayıcısıyla çakışmasın. */
  let startTimer: ReturnType<typeof setTimeout> | null = null;
  let disposed = false;
  const botMoves = [0, 0, 0, 0];
  /** Sohbet için ayrı bir akış: dağıtımın rastgeleliğini bozmasın. */
  const chatRng = makeRng(`${seed}#chat`);
  const lastBotChatAt = [0, 0, 0, 0];

  const seats = (): PublicSeat[] =>
    ([0, 1, 2, 3] as Seat[]).map((seat) => ({
      seat,
      name: seat === HUMAN ? options.name : BOT_NAMES[seat - 1]!,
      connected: true,
      bot: seat !== HUMAN,
      botMoves: botMoves[seat]!,
    }));

  const isBot = (seat: Seat) => seat !== HUMAN;

  function emit(events: GameEvent[]): void {
    if (disposed) return;
    seq += 1;
    for (const event of events) handlers.event(event);
    schedule(); // deadline'ı belirler, bu yüzden görünümden önce
    handlers.view(
      buildViewMessage(match, HUMAN, { seq, seats: seats(), deadline, deadlineTotal }),
    );
  }

  function commit(next: Match, extra: GameEvent[] = []): void {
    const events = [...deriveEvents(match, next), ...extra];
    match = next;
    emit(events);
  }

  function clearTimer(): void {
    if (timer !== null) clearTimeout(timer);
    timer = null;
  }

  /** Geri sayımı kurar. Süre 0 ise (testler) hiç gösterilmez. */
  function countdown(ms: number): void {
    if (ms <= 0) return;
    deadline = Date.now() + ms;
    deadlineTotal = ms;
  }

  /** Sırayı kurar: bot ise gecikmeli oynatır, insan ise geri sayımı başlatır. */
  function schedule(): void {
    clearTimer();
    deadline = null;
    deadlineTotal = null;
    if (disposed || match.phase === 'matchOver') return;

    if (match.phase === 'exchange') {
      const waiting = match.exchange!.pending
        .filter((p) => !match.exchange!.chosen.some((c) => c.from === p.from))
        .map((p) => p.from);
      if (waiting.length === 0) return;

      // Botlar önce ve hızlıca hallolur; insan tam süresini ondan sonra alır.
      // Aynı anda kurulursa insanın sayacı bot bitince baştan başlıyor gibi
      // görünüyor — geri sayım sıçraması oluyordu.
      const bots = waiting.filter(isBot);
      if (bots.length > 0) {
        countdown(botDelayMs);
        timer = setTimeout(() => resolveExchange(bots), botDelayMs);
        return;
      }

      countdown(exchangeMs);
      if (exchangeMs > 0) timer = setTimeout(() => resolveExchange(waiting), exchangeMs);
      return;
    }

    const seat = match.deal!.turn;
    if (isBot(seat)) {
      // Botun düşünme süresi de geri sayım: rakip avatarında halka dönsün.
      countdown(botDelayMs);
      timer = setTimeout(() => playBot(seat), botDelayMs);
      return;
    }
    if (turnMs > 0) {
      countdown(turnMs);
      timer = setTimeout(() => {
        commit(applyMove(match, seat, timeoutMove(match, seat)));
      }, turnMs);
    }
  }

  function playBot(seat: Seat): void {
    if (disposed || match.phase !== 'play') return;
    botMoves[seat] = (botMoves[seat] ?? 0) + 1;
    commit(applyMove(match, seat, chooseMove(publicView(match, seat), 'normal')));
    maybeBotChat(seat);
  }

  /**
   * Botlar ara sıra hazır mesaj yollar — masa sessiz kalmasın ve balonların
   * nerede çıktığı gerçek oyunda da görünsün. Online masada bu işi insanlar
   * yapıyor; offline modda kimse yapmıyordu.
   *
   * Sohbet KUYRUĞA GİRMİYOR: `useGame` `chat` olayını kuyruktan önce yakalıyor,
   * yani mesaj kart animasyonlarının arkasında beklemiyor.
   */
  function maybeBotChat(seat: Seat): void {
    if (!botChat || disposed || seat === HUMAN) return;
    if (chatRng() > BOT_CHAT_CHANCE) return;
    const now = Date.now();
    if (now - lastBotChatAt[seat]! < BOT_CHAT_COOLDOWN_MS) return;
    lastBotChatAt[seat] = now;
    const text = FALLBACK_CHAT_PHRASES[Math.floor(chatRng() * FALLBACK_CHAT_PHRASES.length)];
    if (text) handlers.event({ kind: 'chat', seat, text });
  }

  function resolveExchange(seatsToResolve: Seat[]): void {
    if (disposed || match.phase !== 'exchange') return;
    let next = match;
    let done = false;
    for (const seat of seatsToResolve) {
      const pending = next.exchange!.pending.some(
        (p) => p.from === seat && !next.exchange!.chosen.some((c) => c.from === seat),
      );
      if (!pending) continue;
      next = autoExchange(next, seat);
      if (next.phase === 'play') done = true;
    }
    commit(next, done ? [{ kind: 'exchangeDone' }] : []);
  }

  function begin(): void {
    if (disposed) return;
    startTimer = null;
    emit([
      { kind: 'matchStart', seats: seats() },
      { kind: 'dealStart', dealIndex: 0, leader: match.deal!.turn },
    ]);
  }

  /**
   * Önce MASA görünür, sonra kartlar dağıtılır.
   *
   * Eskiden maç anında başlıyordu ve olay kuyruğu (`matchStart` + `dealStart`)
   * bir saniyeden fazla oynarken ekranda hiçbir şey yoktu — "botlarla oyna"
   * tıklanıyor, bir süre bekleniyor, sonra masa beliriyordu. Şimdi masa hemen
   * çiziliyor, ortada geri sayım dönüyor.
   */
  queueMicrotask(() => {
    if (disposed) return;
    // Yerel oyunda masa zaten dolu ve yöneticilik anlamsız: botlar oyunun
    // kendisi, eklenip çıkarılmıyor.
    handlers.lobby(
      seats().map((s) => ({ name: s.name, bot: s.bot })),
      0,
      // Yerel masanın yöneticisi sensin: bot ekleyecek boş koltuk yok ama
      // maskotu salma yetkisi yöneticiye bağlı.
      true,
    );
    if (startDelayMs <= 0) {
      begin();
      return;
    }
    handlers.starting(Date.now() + startDelayMs);
    startTimer = setTimeout(begin, startDelayMs);
  });

  return {
    send(type, payload) {
      if (disposed) return;
      if (type === 'move') {
        const move = payload as Move;
        const error = checkMove(match, HUMAN, move);
        if (error) {
          handlers.error(error);
          return;
        }
        commit(applyMove(match, HUMAN, move));
        return;
      }
      if (type === 'chat') {
        // Yerel modda karşı taraf yok; mesaj doğrudan kendi ekranına dönüyor.
        // Aynı olay şekli kullanılıyor ki masa bileşeni modu bilmesin.
        const { text } = payload as { text: string };
        if (FALLBACK_CHAT_PHRASES.includes(text)) {
          handlers.event({ kind: 'chat', seat: HUMAN, text });
        }
        return;
      }
      if (type === 'hello') return; // yerel oyunda el sıkışma yok
      if (type === 'mascot') {
        // Yerel oyunda yönetici zaten sensin: olay doğrudan kendi ekranına
        // dönüyor, masa bileşeni yine modu bilmiyor.
        handlers.event({ kind: 'mascot' });
        return;
      }
      if (type === 'exchange') {
        const { cards } = payload as { cards: CardId[] };
        try {
          // Kural motoru doğruluyor; hatalı seçim burada yakalanıyor.
          const next = submitExchange(match, HUMAN, cards);
          commit(next, next.phase === 'play' ? [{ kind: 'exchangeDone' }] : []);
        } catch (cause) {
          handlers.error(cause instanceof Error ? cause.message : 'Geçersiz seçim');
        }
      }
    },
    dispose() {
      disposed = true;
      clearTimer();
      if (startTimer !== null) clearTimeout(startTimer);
      startTimer = null;
    },
  };
}

/**
 * Maç tohumu.
 *
 * `crypto.randomUUID` YALNIZCA güvenli bağlamda var (https ya da localhost).
 * Telefondan yerel sunucuya `http://192.168.x.x:5173` ile bağlanınca bağlam
 * güvenli değil ve çağrı `TypeError` atıyor — botlarla oyna basılınca ekranda
 * boş masa kalmasının sebebi buydu. Yedek yol kriptografik değil ama burada
 * gereken tek şey maçları birbirinden ayırmak.
 */
export function randomSeed(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `local-${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
}
